'use client';

import React from 'react';
import styled from 'styled-components';
import { Skeleton } from '@/components/skeletons';

// ============================================================================
// Styled Components
// ============================================================================

const SkeletonContainer = styled.div<{ $height?: string }>`
  width: 100%;
  height: ${({ $height }) => $height || '400px'};
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const SkeletonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const SkeletonChartArea = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 20px 0;
`;

const SkeletonBar = styled(Skeleton)<{ $height: number }>`
  flex: 1;
  height: ${({ $height }) => $height}%;
`;

const SkeletonLineChart = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SkeletonPieChart = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

const SkeletonLegend = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  margin-top: 20px;
`;

// ============================================================================
// Component
// ============================================================================

export interface ChartSkeletonProps {
  /** Type of chart */
  type?: 'line' | 'bar' | 'pie';
  /** Height of the skeleton */
  height?: string;
  /** Show legend */
  showLegend?: boolean;
  /** Number of bars/data points */
  items?: number;
}

/**
 * ChartSkeleton Component
 * 
 * Loading skeleton for Chart components
 */
export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({
  type = 'line',
  height = '400px',
  showLegend = true,
  items = 8,
}) => {
  const renderChartContent = () => {
    switch (type) {
      case 'bar':
        return (
          <SkeletonChartArea>
            {Array.from({ length: items }).map((_, index) => (
              <SkeletonBar
                key={index}
                $height={Math.random() * 60 + 20}
                animation="pulse"
              />
            ))}
          </SkeletonChartArea>
        );
      
      case 'pie':
        return (
          <SkeletonPieChart>
            <Skeleton
              variant="circular"
              width="250px"
              height="250px"
              animation="pulse"
            />
          </SkeletonPieChart>
        );
      
      case 'line':
      default:
        return (
          <SkeletonLineChart>
            <Skeleton
              width="100%"
              height="80%"
              borderRadius="8px"
              animation="wave"
            />
          </SkeletonLineChart>
        );
    }
  };

  return (
    <SkeletonContainer $height={height}>
      <SkeletonHeader>
        <Skeleton width="150px" height="20px" />
        <Skeleton width="100px" height="32px" />
      </SkeletonHeader>

      {renderChartContent()}

      {showLegend && (
        <SkeletonLegend>
          {Array.from({ length: Math.min(items, 4) }).map((_, index) => (
            <Skeleton key={index} width="80px" height="16px" />
          ))}
        </SkeletonLegend>
      )}
    </SkeletonContainer>
  );
};

export default ChartSkeleton;

